import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../config/api';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { 
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  max-width: 500px;
  width: 100%;
  position: relative;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #636e72;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f2f6;
    color: #2d3436;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3436;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ModalText = styled.p`
  color: #636e72;
  line-height: 1.6;
  margin-bottom: 2rem;
  font-size: 1rem;
`;

const EmailDisplay = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 2rem;
  font-weight: 600;
  color: #667eea;
  border: 2px solid rgba(102, 126, 234, 0.1);
`;

const CodeInputContainer = styled.div`
  margin-bottom: 2rem;
`;

const CodeInput = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 1.2rem;
  text-align: center;
  letter-spacing: 4px;
  font-family: monospace;
  font-weight: 600;
  outline: none;
  transition: all 0.3s ease;
  text-transform: uppercase;
  
  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &.error {
    border-color: #e74c3c;
    box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
  }
`;

const VerifyButton = styled(motion.button)`
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
  }
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: #667eea;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 1rem;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    color: #764ba2;
  }
`;

const ErrorMessage = styled(motion.div)`
  background: #fee;
  color: #e74c3c;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #fadbd8;
  font-size: 0.9rem;
`;

const SuccessMessage = styled(motion.div)`
  background: #eef;
  color: #27ae60;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #d5f4e6;
  font-size: 0.9rem;
`;

const CountdownText = styled.div`
  font-size: 0.8rem;
  color: #95a5a6;
  margin-top: 0.5rem;
`;

const VerificationModal = ({ isOpen, onClose, email, onVerificationSuccess }) => {
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  const handleVerify = async () => {
    if (!code.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setIsVerifying(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API}/api/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          code: code.trim()
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          throw new Error(errorText || `API failed ${response.status}`);
        }
        throw new Error(errorData.error || `API failed ${response.status}`);
      }

      const data = await response.json();
      setSuccess(data.message);
      setTimeout(() => {
        onVerificationSuccess();
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Verification error:', error);
      setError(error.message || 'Network error. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setResendCooldown(60);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          throw new Error(errorText || `API failed ${response.status}`);
        }
        throw new Error(errorData.error || `API failed ${response.status}`);
      }

      const data = await response.json();
      setSuccess('New verification code sent!');
    } catch (error) {
      console.error('Resend error:', error);
      setError(error.message || 'Network error. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ opacity: 0, scale: 0.9, y: -50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -50 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={onClose}>×</CloseButton>
            
            <ModalTitle>Verify Your Email</ModalTitle>
            
            <ModalText>
              We've sent a verification code to:
            </ModalText>
            
            <EmailDisplay>{email}</EmailDisplay>
            
            <ModalText>
              Please enter the 6-character code below to complete your subscription.
            </ModalText>

            {error && (
              <ErrorMessage
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {error}
              </ErrorMessage>
            )}

            {success && (
              <SuccessMessage
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {success}
              </SuccessMessage>
            )}

            <CodeInputContainer>
              <CodeInput
                type="text"
                placeholder="XXXXXX"
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                  if (value.length <= 6) {
                    setCode(value);
                    setError('');
                  }
                }}
                onKeyPress={handleKeyPress}
                className={error ? 'error' : ''}
                maxLength={6}
              />
            </CodeInputContainer>

            <VerifyButton
              onClick={handleVerify}
              disabled={isVerifying || code.length !== 6}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isVerifying ? 'Verifying...' : 'Verify Email'}
            </VerifyButton>

            <ResendButton
              onClick={handleResend}
              disabled={resendCooldown > 0}
            >
              {resendCooldown > 0 
                ? `Resend code in ${resendCooldown}s` 
                : 'Resend verification code'
              }
            </ResendButton>

            <CountdownText>
              Code expires in 15 minutes
            </CountdownText>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default VerificationModal;
